import { useEffect, useMemo } from "react";
import { Link } from "@tanstack/react-router";
import { Archive, ArchiveRestore, MessagesSquare, Plus } from "lucide-react";

import { EmptyState, PageHeader } from "@/components/common/FeatureShell";
import { markContentVisible } from "@/hooks/useNavPerf";
import { Button } from "@/components/ui/button";
import { useChatHistory } from "@/contexts/ChatHistoryContext";
import { useProjects } from "@/hooks/queries/useProjects";
import { type ChatRow } from "@/lib/api";
import { cn } from "@/lib/utils";

function relativeTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const ms = Date.now() - Date.parse(iso);
  if (Number.isNaN(ms)) return "";
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

/**
 * All chats — the full, paginated chat history.
 *
 * Source of truth is the shared ChatHistoryContext (same data the rail "Recent
 * chats" peek reads), so the two never drift. This page owns the management
 * affordances the peek omits: the active/archived filter, load-more, and
 * archive/unarchive. Each row is labelled with its matter (or "No matter").
 */
export function ChatsPageClient() {
  const { chats, hasMoreChats, loadMoreChats, showArchived, setShowArchived, archiveChat, unarchiveChat } = useChatHistory();
  const { data: projects } = useProjects();

  const matterName = useMemo(() => new Map((projects ?? []).map((project) => [project.id, project.name])), [projects]);

  useEffect(() => {
    markContentVisible(true);
  }, []);

  return (
    <div className="min-h-screen">
      <PageHeader
        title="All chats"
        description="Every chat you can access, across all matters."
        actions={
          <Button type="button" asChild>
            <Link to="/assistant">
              <Plus className="size-4" />
              New chat
            </Link>
          </Button>
        }
      />
      <main className="p-4 md:p-6">
        <div className="mb-4 flex w-fit rounded-md border bg-card p-1">
          {([
            { key: false, label: "Active" },
            { key: true, label: "Archived" },
          ] as const).map((option) => (
            <button
              key={option.label}
              type="button"
              onClick={() => setShowArchived(option.key)}
              className={cn("h-9 rounded px-3 text-sm", showArchived === option.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted")}
            >
              {option.label}
            </button>
          ))}
        </div>
        {chats.length ? (
          <div className="overflow-hidden rounded-md border bg-card">
            {chats.map((chat) => (
              <ChatListRow
                key={chat.id}
                chat={chat}
                matterLabel={chat.project_id ? matterName.get(chat.project_id) ?? "Matter" : "No matter"}
                showArchived={showArchived}
                onArchive={() => void archiveChat(chat.id)}
                onUnarchive={() => void unarchiveChat(chat.id)}
              />
            ))}
            {hasMoreChats ? (
              <div className="p-2">
                <Button variant="ghost" size="sm" className="w-full justify-start" onClick={() => void loadMoreChats()}>
                  Load more
                </Button>
              </div>
            ) : null}
          </div>
        ) : (
          <EmptyState
            icon={<MessagesSquare className="size-5" />}
            title={showArchived ? "No archived chats" : "No chats yet"}
            description={showArchived ? "Archived chats will appear here." : "Start a chat or open a matter to begin."}
          />
        )}
      </main>
    </div>
  );
}

function ChatListRow({
  chat,
  matterLabel,
  showArchived,
  onArchive,
  onUnarchive,
}: {
  chat: ChatRow;
  matterLabel: string;
  showArchived: boolean;
  onArchive: () => void;
  onUnarchive: () => void;
}) {
  const title = <span className="block truncate font-medium">{chat.title || "Untitled chat"}</span>;
  const timestamp = relativeTime(chat.updated_at ?? chat.created_at);
  const meta = (
    <span className="flex items-center gap-2 text-xs text-muted-foreground">
      <span className="truncate">{matterLabel}</span>
      {chat.project_id ? (
        <span className={cn("shrink-0 rounded-full px-2 py-0.5 font-medium", chat.shared_at ? "bg-primary/10 text-primary" : "bg-muted")}>
          {chat.shared_at ? "Shared" : "Private"}
        </span>
      ) : null}
      {timestamp ? <span className="ml-auto shrink-0 tabular-nums">{timestamp}</span> : null}
    </span>
  );
  const content = (
    <span className="min-w-0 flex-1">
      {title}
      {meta}
    </span>
  );

  return (
    <div className="flex min-h-14 items-center gap-2 border-b px-3 py-2 text-sm last:border-b-0 hover:bg-muted/60">
      {chat.project_id ? (
        <Link to="/projects/$id/assistant/chat/$chatId" params={{ id: chat.project_id, chatId: chat.id }} className="flex min-w-0 flex-1 items-center">
          {content}
        </Link>
      ) : (
        <Link to="/assistant/chat/$id" params={{ id: chat.id }} className="flex min-w-0 flex-1 items-center">
          {content}
        </Link>
      )}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="size-8 shrink-0"
        aria-label={showArchived ? "Unarchive chat" : "Archive chat"}
        onClick={showArchived ? onUnarchive : onArchive}
      >
        {showArchived ? <ArchiveRestore className="size-4" /> : <Archive className="size-4" />}
      </Button>
    </div>
  );
}
